/**
 *  @file  Module that implements Compute API
 *  @module dcp/compute
 *  @access public
 */

/* global dcpConfig */

/**
 * @access private - Will change soon!
 * @typedef {object} JobRequirements
 * @property {object} environment
 * @property {boolean} environment.fdlibm - Workers express capability 'fdlibm' in order for client applications to have confidence that results will be bitwise identical across workers. This library is recommended, but not required, for implementaiton of Google chrome, Mozilla Firefox and the DCP standalone worker use fdlibm but Microsoft Edge and Apple Safari do not use it.
 * @property {boolean} environment.offscreenCanvas When present, this capability indicates that the worker environment has the OffscreenCanvas symbol defined.
 * @property {boolean} environment.webgpu When present, this capability indicates that the worker environment has webgpu.
 * @property {object} engine
 * @property {boolean} engine.browser This capability enforces that the worker is running in a browser.
 * @property {boolean} engine.spidermonkey The capability enforces that the worker will run in the SpiderMonkey JS engine.
 * @property {boolean} browser.v8 The capability enforces that the worker will run in the V8 JS engine.
 */

/**
 * This JSDoc 'namespace' is a convenient way to document some globals that are available only in
 * the sandbox environment provided to work functions.
 * @access public
 * @namespace sandboxEnv
 */

/**
 * This function emits a progress event. Progress events should be emitted approximately once per second; a task which fails to emit a 
 * progress event for a certain period of time will be cancelled by the supervisor. The argument to this function is interpreted to six 
 * significant digits, and must increase for every call. *All work functions must emit at least one progress event* - this requirement 
 * will be enforced by the estimator. * The period of time mentioned above will be at least 30 wall-clock seconds and at least 30 
 * benchmark-adjusted seconds
 * @access public
 * @method progress
 * @param {string|number|undefined} n  a number between 0 and 1 (inclusive) that represents a best-guess 
 * at the completed portion of the task as a ratio of completed work to total work. If the argument is a string ending in the `%` symbol, 
 * it will be interpreted as a number in the usual mathematical sense. If the argument is `undefined`, the slice progress is noted but 
 * the amount of progress is considered to be indeterminate.
 * @memberof module:dcp/compute~sandboxEnv
 * @returns {boolean} true
 */

/**
 * Emit a `console` event to the `JobHandle` in the client. If the sandbox is running in an environment with a native console object, the native method may also be invoked.
 * 
 * @member console
 * @property {function} log fire console event with 'log' as `level` (see {@link Job#event:console|Job#event:console.level}).
 * @property {function} debug fire console event with 'debug' as `level` (see {@link Job#event:console|Job#event:console.level}).
 * @property {function} info fire console event with 'info' as `level` (see {@link Job#event:console|Job#event:console.level}).
 * @property {function} warn fire console event with 'warn' as `level` (see {@link Job#event:console|Job#event:console.level}).
 * @property {function} error fire console event with 'error' as `level` (see {@link Job#event:console|Job#event:console.level}).
 * 
 * **Note** some ES environments (Chrome, Firefox) implement C-style print formatting in this method. This is currently not supported.
 * @memberof module:dcp/compute~sandboxEnv
 * @emits Job#console
 * @access public
 */

/**
 * @member work An object that contains the following properties
 * @property {function} emit (eventName, ...) - send arbitrary args with `eventName`. This event is received client-side with those args (see {@link Job#work}).
 * @property {object} job
 * @property {object} job.public Property set on job handle, see {@link Job#public}.
 * @memberof module:dcp/compute~sandboxEnv
 * @access public
 */

/**
 * @member {OffscreenCanvas} OffscreenCanvas As defined in the {@link https://developer.mozilla.org/en-US/docs/Web/API/OffscreenCanvas|HTML Standard}, provides a canvas which can be rendered off-screen. If this interface is not available in a given worker, the worker will not report capability "offscreenCanvas".
 * @memberof module:dcp/compute~sandboxEnv
 * @access public
 */

const protocolV4 = require('dcp/protocol');
const { EventEmitter } = require('dcp/common/dcp-events/event-emitter');
const { RangeObject, rehydrateRange } = require('dcp/dcp-client/range-object');
const { RemoteDataSet } = require('dcp/dcp-client/remote-data-set');
const { RemoteDataPattern } = require('dcp/dcp-client/remote-data-pattern');
const { Job, ResultHandle } = require('dcp/dcp-client/job');
const { jobStatuses } = require('dcp/common/scheduler-constants');
const wallet = require('dcp/dcp-client/wallet');
const { DcpURL } = require('dcp/common/dcp-url');
const { DCPError } = require('dcp/common/dcp-error');
const debugging = require('dcp/debugging').scope('dcp-client');
const { getTransientConnection }  = require('dcp/utils/transient-connections');
const jnu = require('dcp/dcp-client/jnu');

const debug = (...args) => {
  if (debugging()) {
    args.unshift('dcp-client:compute');
    console.debug(...args);
  }
};

const MARKET_VALUE = Symbol('Market Value');

/**
 * Instance methods of this class are exported as static members of {@link module:dcp/compute|dcp/compute}.
 * @example
 * let job = compute.for(1, 3, function (i) {
 *   progress('100%');
 *   return i*10;
 * })
 * let results = await job.exec();
 */
class Compute extends EventEmitter {
  constructor () {
    super('Compute');

    Object.defineProperty(this.marketValue, MARKET_VALUE, {
      value: true,
      writable: false,
      configurable: false,
      enumerable: false,
    });

    const ceci = this;
    this.RemoteDataSet = RemoteDataSet;
    this.RemoteDataPattern = RemoteDataPattern;
    // Initialize to null so these properties are recognized for the Job class

    Object.defineProperty(exports, 'phemeConnection', {
      get: () => { 
        return getTransientConnection(dcpConfig.scheduler.services.pheme, 10);
      },
    });

    Object.defineProperty(exports, 'deployConnection', {
      get: () => { 
        return getTransientConnection(dcpConfig.scheduler.services.jobSubmit, 10);
      },
    });
  }

  /** Return a special object that represents the current market value
   *  @param {number} factor - OPTIONAL multiplier. default: 1.0
   */
  marketValue (factor = 1) {
    return {
      [MARKET_VALUE]: factor
    };
  }

  /** Describes the payment required to compute such a slice on a worker
   *  or in a market working at the rates described in the WorkValue object.
   *  This function does not take into account job-related overhead.
   * 
   * @param {object} sliceProfile A SliceProfile-shaped object
   * @param {object} workValue A WorkValue-shaped object
   * @returns {number} Payment value in DCC describing rate to compute slice
   */
  calculateSlicePayment(sliceProfile, workValue) {
    return sliceProfile.cpuHours * workValue.cpuHour
         + sliceProfile.gpuHours * workValue.gpuHour
         + sliceProfile.inputMBytes * workValue.inputMByte
         + sliceProfile.outputMBytes * workValue.outputMByte;
  }

  
  /**
   * Form 1. Create a Job for the distributed computer by specifying the start,
   * end, and step for a RangeObject with a given work function and arguments.
   *
   * @param {number} start First number in a range  (see {@link RangeObject})
   * @param {number} end Last number in a range  (see {@link RangeObject})
   * @param {number} [step=1] the space between numbers in a range  (see
   * {@link RangeObject})
   * @param {function | string | URL | DcpURL} work Work function as string or
   * function literal.
   * @param {object} [arguments=[]] optional Array-like object which contains
   * arguments that are passed to the work function
   * @returns {Job}
   * @access public
   * @example
   * let job = compute.for(1, 3, function (i) {
   *   progress('100%')
   *   return i*10
   * })
   */
  /**
   * Form 2.a Create a Job for the distributed computer by specifying the input
   * as a RemoteDataSet with a given work function and arguments.
   *
   * @param {RemoteDataSet} remoteDataSet Array, ES6 function* generator, or any
   * other type of iterable object.
   * 
   * Form 2.b Create a Job for the distributed computer by specifying the input
   * as a RemoteDataPattern with a given work function and arguments.
   * @param {RemoteDataPattern} RemoteDataPattern Array, ES6 function* generator, or any
   * other type of iterable object.
   * @param {function | string | URL | DcpURL} work Work function as string or
   * function literal.
   * @param {object} [arguments=[]] optional Array-like object which contains
   * arguments that are passed to the work function
   * @returns {Job}
   * @access public
   * @example
   * const remoteData = ['https://example.com/data'];
   * const job = compute.for(new RemoteDataSet(...remoteData), function(i) {
   *   progress(1)
   *   return i
   * })
   * 
   * * @example
   * const pattern = 'https://example.com/data/{slice}.json';
   * const NumberOfSlices = 5;
   * const job = compute.for(new RemoteDataSet(pattern, NumberOfSlices), function(i) {
   *   progress(1)
   *   return i
   * })
   */
  /**
   * Form 3. Create a Job for the distributed computer by specifying the input
   * as an Iterable object with a given work function and arguments.
   *
   * @param {Iterable} iterableObject Array, ES6 function* generator, or any
   * other type of iterable object.
   * @param {function | string | URL | DcpURL} work Work function as string or
   * function literal.
   * @param {object} [arguments=[]] optional Array-like object which contains
   * arguments that are passed to the work function
   * @returns {Job}
   * @access public
   * @example
   * let job = compute.for([123,456], function(i) {
   *   progress(1)
   *   return i
   * })
   */
  /**
   * Form 4. Create a Job for the distributed computer by specifying the input
   * as a RangeObject with a given work function and arguments.
   *
   * @param {RangeObject} rangeObject Either a RangeObject or an object literal
   * which can be used to create a RangeObject (see {@link RangeObject}).
   * @param {function | string | URL | DcpURL} work Work function as string or
   * function literal.
   * @param {object} [arguments=[]] optional Array-like object which contains
   * arguments that are passed to the work function
   * @returns {Job}
   * @access public
   * @example
   * let job = compute.for({start: 10, end: 13, step: 2}, (i) => progress(1) && i)
   */
  /**
   * Form 5. Similar to Form 4, except with a multi range object containing an
   * array of range objects in the key ranges. These are used to create
   * multi-dimensional ranges, like nested loops. If they were written as
   * traditional loops, the outermost loop would be the leftmost range object,
   * and the innermost loop would be the rightmost range object.
   *
   * @param {MultiRangeObject} multiRange Either a MultiRangeObject or any valid
   * argument to the constructor.
   * @param {function | string | URL | DcpURL} work Work function as string or
   * function literal.
   * @param {object} [arguments=[]] optional Array-like object which contains
   * arguments that are passed to the work function
   * @returns {Job}
   * @access public
   * @example
   * let job = compute.for([{start: 1, end: 2}, {start: 3, end: 5}], function(i,j) {
   *   return [i, j, i*j]
   * })
   */
  for(...args) {
    // args, or work function
    const lastArg = args[args.length - 1];
    // work function, if args provided
    const secondLastArg = args[args.length - 2];
    let range = null;
    let work;
    let genArgs = [];

    // All forms: extract work and optional args from the end and validate it.
    if (isValidWorkFunctionType(secondLastArg)) {
      // for(..., workFunction, arguments)
      work = secondLastArg
      if (Number.isInteger(lastArg?.length) && lastArg?.length >= 0) {
        genArgs = lastArg;
      } else if (lastArg === undefined) {
        genArgs = [];
      } else {
        throw new TypeError(
          'Work function arguments (the last parameter), should be an array.',
        );
      }
    } else if (isValidWorkFunctionType(lastArg)) {
      // for(..., workFunction)
      work = lastArg;
    }

    if (!isWorkFunctionValid(work)) {
      throw new TypeError(
        `Work parameter must be a function or a string that evaluates to a function, or a URL that points to the location of a function.\nReceived type ${typeof work} (${work})`,
      );
    }

    // Extract the proper information from every form.
    const [firstArg, secondArg, thirdArg] = args;
    if (['object', 'string'].includes(typeof firstArg)) {
      // Forms 2 & 3 (data is iterable, eg. an Array or RemoteDataSet)
      // Forms 4 & 5 (data is RangeObject & Friends)
      range = rehydrateRange(firstArg);
    } else if (typeof firstArg === 'number' && typeof secondArg === 'number') {
      // Form 1 (data is start, end[, step])
      const start = firstArg;
      const end = secondArg;

      /**
       * If there is an argument between the end and the work fn, use it as step
       * otherwise set step to undefined.
       */
      const step = typeof thirdArg === 'number' ? thirdArg : undefined;
      range = new RangeObject(start, end, step);
    } else {
      throw new TypeError(
        'Parameters do not match one of the accepted call forms.',
      );
    }

    debug('Work:', work);
    debug('Input Set:', (range instanceof RangeObject || range instanceof Function) ? range : (Array.isArray(range) ? range.length + ' elements' : '<yes>'));
    debug('Arguments:', genArgs.length + ' elements');
    return new Job(work, range, genArgs);

    /**
     * @param {any} workFunction
     */
    function isValidWorkFunctionType(workFunction) {
      return (
        typeof workFunction === 'function' ||
        typeof workFunction === 'string' ||
        DcpURL.isURL(workFunction)
      );
    }

    /**
     * Checks if the workFunction can be evaluated to a function or if it's
     * protocol is HTTPS,
     * @param {function | string | URL | DcpURL} workFunction
     */
    function isWorkFunctionValid(workFunction) {
      switch (typeof workFunction) {
        case 'function':
        case 'string':
          return true;
        case 'object':
          return DcpURL.isURL(workFunction);
        default:
          return false;
      }
    }
  }

  /**
   * Form 1.
   * Create a job which will run a work function once
   *
   * @param {function | string | URL | DcpURL} work Work function as string or function literal.
   * @param {object} [workFunctionArgs=[]] optional Array-like object which contains arguments which are passed to the work func
   * @returns {Job}
   * @access public
   * @example
   * const job = compute.do(function (i) {
   *   progress('100%')
   *   return i*10
   * })
   */
  /**
   * Form 2.
   * Create a job which will run a work function on the values 1..n
   *
   * @param {number} n Number of times to run the work function.
   * @param {function | string | URL | DcpURL} work Work function as string or function literal.
   * @param {object} [workFunctionArgs=[]] optional Array-like object which contains arguments which are passed to the work func
   * @returns {Job}
   * @access public
   * @example
   * const job = compute.do(5, function (i) {
   *   progress('100%')
   *   return i*10
   * })
   */
  do(...args) {
    let n;
    let work;
    let workFunctionArgs;
    if (typeof args[0] !== 'number') {
      // Form 1.
      n = 1;
      [work, workFunctionArgs] = args;
    } else {
      // Form 2.
      [n, work, workFunctionArgs] = args;
    }
    return this.for(0, n - 1, work, workFunctionArgs);
  }

  /** Cancel a running job
   *
   * @param {string | Job} firstArg Address or JobHandle of the job to cancel.
   * @param {wallet.Keystore} ownerKeystore The keystore of the job owner
   * @param {string} reason If provided, will be sent on to client
   * @returns {Promise} which resolves to an {object} containg the status of the funds that were released
   *          from escrow, or rejects with {Error} when we can't communicate with the jobSubmit service.
   * @throws when id or ownerKeystore are not provided.
   * @access public
   */
  async cancel (firstArg, ownerKeystore, reason = '') {
    if (typeof firstArg !== 'string' && !(firstArg instanceof Job)) 
      throw new Error(`compute.cancel: first argument must be a string or Job, not ${typeof firstArg}.`);
    
    const id = firstArg instanceof Job ? firstArg.id : firstArg;

    if (!(ownerKeystore instanceof wallet.Keystore)) {
      throw new Error('compute.cancel: ownerKeystore must be instance of wallet.Keystore');
    }

    const response = await exports.deployConnection.request('cancelJob', {
      job: id,
      owner: ownerKeystore.address,
      reason,
    }, ownerKeystore);

    return response.payload;
  }

  /**
   * Reconnect/resume a job, by opaque id
   *
   * @param {string} id Opaque id of the Job to resume/reconnect.
   * @param {wallet.Keystore} [ownerKeystore=wallet.get()] The keystore of the job owner
   * @returns {Job}
   * @throws when id is not provided.
   * @access public
   */
  async resume (id, ownerKeystore) {
    if (typeof id !== 'string') {
      throw new Error(`compute.resume: Job ID must be a string, not ${typeof id}.`);
    }

    if (!(ownerKeystore instanceof wallet.Keystore)) {
      ownerKeystore = await wallet.get();
    }

    const response = await this.schedulerConnection.request('fetchJob', {
      job: id,    // XXX@TODO change 'address' to 'job' once we port this operation to v4
      owner: ownerKeystore.address,
    }, ownerKeystore);

    const job = new Job(response.payload);
    job.setPaymentAccountKeystore(ownerKeystore);
    return job;
  }

  /**
   * Form 1. Query the status of a job that has been deployed to the scheduler.
   *
   * @access public
   * @param {string|Job} job A job ID or a Job instance
   * @throws when ownerKeystore is not provided
   * @returns {Promise<object>} A status object describing a given job.
   */
  /**
   * Form 2. Query the status of jobs deployed to the scheduler.
   *
   * @access public
   * @param {object} [opts] Query options
   * @param {number} [opts.startTime] Jobs older than this will be excluded from
   * the results.
   * @param {number} [opts.endTime] Jobs newer than this will be excluded from
   * the results.
   * @throws when ownerKeystore is not provided
   * @returns {Promise<Array<Object>>} An array of status objects corresponding to the status of
   * jobs deployed by the given payment account.
   */
  async status(...args) {
    const requestPayload = {};

    const firstArg = args.shift();
    if (typeof firstArg === 'string' || firstArg instanceof Job) {
      // Form 1
      requestPayload.job = firstArg instanceof Job ? firstArg.id : firstArg;
      requestPayload.statuses = jobStatuses;
    } else if (typeof firstArg === 'object') {
      // Form 2
      if (
        (typeof firstArg.startTime !== 'undefined' &&
          typeof firstArg.startTime !== 'number' &&
          !(firstArg.startTime instanceof Date)) ||
        (typeof firstArg.endTime !== 'undefined' &&
          typeof firstArg.endTime !== 'number' &&
          !(firstArg.endTime instanceof Date))
      ) {
        throw new TypeError(
          'startTime and or endTime were not specified as instances of Date or milliseconds since epoch',
        );
      }

      requestPayload.startDeployTime = firstArg.startTime;
      requestPayload.endDeployTime = firstArg.endTime;
      
      if (typeof firstArg.statuses !== 'undefined'
        && (!(firstArg.statuses instanceof Array)
        || !(firstArg.statuses.every((status) => jobStatuses.includes(status))))
      ) {
          throw new TypeError(`Job statuses must be specified in an Array and can only include ${jobStatuses}`)
      }
      
      requestPayload.statuses = firstArg.statuses || jobStatuses;
      
    } else {
      throw new TypeError(`Unknown first argument type ${typeof firstArg}`);
    }

    const {
      success,
      payload: responsePayload,
    } = await exports.phemeConnection.request(
      'listJobs',
      requestPayload
    );

    if (!success) {
      throw new DCPError('Request for job status failed', responsePayload);
    }


    if (responsePayload.length > 0 && (typeof firstArg === 'string' || firstArg instanceof Job)) {
      return responsePayload[0];
    }

    return responsePayload;
  }

  /**
   * Returns information for the job with the provided ID. It will use
   * `wallet.getId()` to retrieve the auth keystore.
   *
   * @param {string} jobId
   * @returns {Promise<object>} status object describing a given job
   * @access public
   */
  async getJobInfo(jobId)
  {
    const id = await wallet.getId();
    const db = await jnu.connect('scheduler', id);
    const jobData = await db.select({
      table: 'jobs',
      prototype: { opaqueId: jobId },
    });
    return jobData.rows[0] || { error: 'Unable to find job' };
  }
}

exports.RemoteDataSet = RemoteDataSet;
exports.RemoteDataPattern = RemoteDataPattern;
exports.MARKET_VALUE = MARKET_VALUE;
exports.compute = new Compute(); /* :( */
exports.compute.getPublicComputeGroupId = () => require('dcp/common/scheduler-constants').computeGroups.public.id;
exports.compute.getPublicComputeGroupDescriptor = () => require('dcp/common/scheduler-constants').computeGroups.public;
exports.compute.ResultHandle = ResultHandle; // export as compute.ResultHandle for instanceof checking
exports.compute.Job = Job;
exports.version = {
  api: '1.5.2',
  provides: '1.0.0'  /* dcpConfig.scheduler.compatibility.operations.compute */
};
